// ABOUTME: Dialog that creates a square pack and populates it with AI-generated squares in one flow.
// ABOUTME: AI generation is the primary path; "Skip — start blank" creates an empty pack to fill in manually.
import { useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Switch from '@mui/material/Switch'
import CloseIcon from '@mui/icons-material/Close'
import { useAiSquareGeneration } from '../hooks/useAiSquareGeneration'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (packId: string) => void
  onCreate: (title: string, randomCalled: boolean) => Promise<{ id: string }>
  onGenerate: (packId: string, topic: string) => Promise<string[]>
  onAddSquares: (packId: string, titles: string[]) => Promise<void>
}

export function CreateSquarePackDialog({ open, onClose, onCreated, onCreate, onGenerate, onAddSquares }: Props) {
  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState(false)
  const [randomCalled, setRandomCalled] = useState(false)
  const [topic, setTopic] = useState('')
  const [topicTouched, setTopicTouched] = useState(false)
  const [packId, setPackId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const packIdRef = useRef<string | null>(null)

  const {
    state,
    suggestions,
    addError,
    generate: hookGenerate,
    addToPack,
    removeSuggestion,
    retry,
    reset: resetHook,
  } = useAiSquareGeneration({
    onGenerate: (t) => onGenerate(packIdRef.current!, t),
    onAddSquares: (titles) => onAddSquares(packIdRef.current!, titles),
  })

  function handleTitleChange(value: string) {
    setTitle(value)
    setTitleError(false)
    if (!topicTouched) setTopic(value)
  }

  function handleTopicChange(value: string) {
    setTopic(value)
    setTopicTouched(true)
  }

  function handleClose() {
    setTitle('')
    setTitleError(false)
    setRandomCalled(false)
    setTopic('')
    setTopicTouched(false)
    setPackId(null)
    setCreating(false)
    setCreateError(null)
    packIdRef.current = null
    resetHook()
    onClose()
  }

  function finishWith(id: string) {
    onCreated(id)
    handleClose()
  }

  async function handleGenerateClick() {
    if (!title.trim()) {
      setTitleError(true)
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const pack = await onCreate(title, randomCalled)
      packIdRef.current = pack.id
      setPackId(pack.id)
      await hookGenerate(topic)
    } catch {
      setCreateError('Something went wrong creating the pack. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  async function handleSkipClick() {
    if (packIdRef.current) {
      finishWith(packIdRef.current)
      return
    }
    if (!title.trim()) {
      setTitleError(true)
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const pack = await onCreate(title, randomCalled)
      finishWith(pack.id)
    } catch {
      setCreateError('Something went wrong creating the pack. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  async function handleAddToPackClick() {
    const success = await addToPack()
    if (success) finishWith(packIdRef.current!)
  }

  const onFormStep = packId === null

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New Square Pack</DialogTitle>
      <DialogContent>
        {onFormStep && (
          <Stack spacing={2} pt={1}>
            {createError && <Alert severity="error">{createError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              error={titleError}
              helperText={titleError ? 'Title is required' : undefined}
              disabled={creating}
            />
            <TextField
              fullWidth
              label="Topic"
              value={topic}
              onChange={(e) => handleTopicChange(e.target.value)}
              placeholder='e.g. "office bingo" or "90s TV shows"'
              helperText="Used to generate square suggestions with AI. Defaults to the title."
              disabled={creating}
            />
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={randomCalled}
                    onChange={(e) => setRandomCalled(e.target.checked)}
                    inputProps={{ 'aria-label': 'random-called' }}
                    disabled={creating}
                  />
                }
                label="Random Called"
              />
              <FormHelperText>You choose which square to call each round. Enable to call squares in a random order automatically.</FormHelperText>
            </FormControl>
            {creating && (
              <Box display="flex" justifyContent="center" pt={1}>
                <CircularProgress />
              </Box>
            )}
          </Stack>
        )}

        {!onFormStep && state === 'generating' && (
          <Box display="flex" justifyContent="center" pt={3}>
            <CircularProgress />
          </Box>
        )}

        {!onFormStep && state === 'reviewing' && (
          <Stack spacing={1}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <List dense>
              {suggestions.map((s, i) => (
                <ListItem
                  key={i}
                  secondaryAction={
                    <IconButton aria-label="remove" edge="end" onClick={() => removeSuggestion(i)}>
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={s} />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}

        {!onFormStep && state === 'adding' && (
          <Box display="flex" justifyContent="center" pt={3}>
            <CircularProgress />
          </Box>
        )}

        {!onFormStep && state === 'error' && (
          <Alert severity="error">
            Something went wrong generating suggestions. Please try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {onFormStep && (
          <>
            <Button onClick={handleSkipClick} disabled={creating}>
              Skip — start blank
            </Button>
            <Button onClick={handleGenerateClick} disabled={creating} variant="contained">
              Generate with AI
            </Button>
          </>
        )}
        {!onFormStep && state === 'reviewing' && (
          <Button onClick={handleAddToPackClick} variant="contained">
            Add to Pack
          </Button>
        )}
        {!onFormStep && state === 'error' && (
          <>
            <Button onClick={() => handleSkipClick()}>
              Skip — start blank
            </Button>
            <Button onClick={() => retry()} variant="contained">
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
